import { useState, useEffect } from "react";
import { useGlobalAPI } from "../../../../hooks/useGlobalAPI";

/**
 * Hook for fetching filtered object details using the global filter system.
 *
 * @param {string} activeFile - The active OCEL file type
 * @param {string} objectType - Optional object type to filter by
 * @param {number} limit - Max objects to return (default: 100)
 * @param {number} offset - Pagination offset (default: 0)
 * @returns {{ ocelData: Object|null, loading: boolean, error: string|null }}
 */
export const useObjectDetails = (activeFile, objectType = null, limit = 100, offset = 0) => {
  const api = useGlobalAPI(activeFile);
  const [ocelData, setOcelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchOcelData = async () => {
      if (!activeFile) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Use POST endpoint with global filter support
        const data = await api.post('/ocel/object-details', {
          object_type: objectType,
          limit,
          offset
        });

        if (!cancelled) {
          // Transform response to match expected format
          if (data && data.objects) {
            setOcelData({
              objects: data.objects,
              total_count: data.total_count,
              object_type: data.object_type,
              limit: data.limit,
              offset: data.offset
            });
          } else {
            setOcelData(data);
          }
        }
      } catch (err) {
        console.error("useObjectDetails: Failed to fetch OCEL data", err);
        if (!cancelled) {
          setError(`Failed to load OCEL data: ${err.message}`);
          setOcelData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchOcelData();

    return () => {
      cancelled = true;
    };
  }, [activeFile, objectType, limit, offset, api.currentFilters]);

  return { ocelData, loading, error };
};
